/**
 * PushEnableBanner — Dashboard'ın en üstünde beliren tek satır CTA.
 *
 * Tetiklenme koşulları (birlikte hepsi):
 *   - Tarayıcı push destekliyor (Web Push API + Notification API + SW)
 *   - Kullanıcı henüz push abone değil (getCurrentSubscription null)
 *   - Notification izni "denied" değil (denied ise ayrı ipucu gösteriyoruz)
 *   - Kullanıcı bu banner'ı "sonra" ile kapatmadıysa
 *
 * Bir kez enable → banner kaybolur (subscribed=true), sonsuza kadar.
 * "Sonra" (dismiss) → localStorage'da 7 gün ertelenir.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Sparkles } from "lucide-react";
import {
  isSupported as isPushSupported,
  getCurrentSubscription,
  subscribe as subscribePush,
} from "../../lib/push";

const DISMISS_KEY = "serpix:push_banner_dismissed_until";
const SNOOZE_DAYS = 7;

function isSnoozed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return parseInt(raw, 10) > Date.now();
  } catch {
    return false;
  }
}

function snooze() {
  const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  try {
    localStorage.setItem(DISMISS_KEY, String(until));
  } catch {
    /* private mode / storage disabled — safe to ignore */
  }
}

export default function PushEnableBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // First-mount check — decides whether to render the banner.
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      if (isSnoozed()) return;
      // Blocked (permission=denied) → still show, but with different CTA copy.
      const perm = typeof Notification !== "undefined" ? Notification.permission : "default";
      if (perm === "granted") {
        // Permission granted — check if subscription exists. If not,
        // resubscribe silently so the user doesn't have to click again.
        const sub = await getCurrentSubscription();
        if (sub) return; // already subscribed
      }
      if (!cancelled) setVisible(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible || dismissed) return null;

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Bildirimler açıldı 🎉");
      setDismissed(true);
    } catch (err) {
      toast.error(err.message || "Bildirimler etkinleştirilemedi", { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const later = () => {
    snooze();
    setDismissed(true);
  };

  return (
    <div
      data-testid="push-enable-banner"
      className="relative overflow-hidden rounded-2xl border border-violet-300 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 p-4 text-white shadow-xl shadow-violet-500/30"
    >
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-8 left-1/3 h-24 w-24 rounded-full bg-white/10 blur-3xl" />
      <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
            <Bell className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-sm font-semibold">
              Bildirimler kapalı
              <Sparkles className="h-3.5 w-3.5 text-yellow-200" />
            </div>
            <p className="text-[11px] leading-relaxed text-white/85">
              Sana atanmış görev, gelen mesaj, ve öne çıkan duyurular için
              anlık bildirim al. PWA olarak ana ekranına eklediysen native
              app gibi davranır — Serpix&apos;i açık tutmadan bile ulaşırız.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            data-testid="push-enable-btn"
            className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-violet-900 shadow transition-colors hover:bg-white/90 disabled:opacity-70"
          >
            {busy ? "Açılıyor..." : "Bildirimleri Aç"}
          </button>
          <button
            type="button"
            onClick={later}
            data-testid="push-later-btn"
            className="rounded-lg border border-white/30 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur transition-colors hover:bg-white/10"
          >
            Sonra
          </button>
          <button
            type="button"
            onClick={later}
            aria-label="Kapat"
            data-testid="push-dismiss-btn"
            className="rounded-md p-1 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
